/**
 * Setpoint scenarios — load against outdoor temperature, with the fitted kink.
 *
 * Each dot is one day of the window: mean load against mean outdoor
 * temperature. The solid line is the fitted relationship, flat until the
 * observed cooling point and rising after it. Selecting a scenario slides that
 * kink to the right; the shaded wedge between the two lines is the load that
 * would not have run.
 *
 * Daily means, not hourly: hourly scatter is dominated by the schedule, and
 * daily is the envelope the changepoint was fitted in.
 *
 * The ceiling marks where OUR ESTIMATE stops being trustworthy, not where
 * comfort ends. Marginal return per degree stays flat out to 15 degF on real
 * sites, so energy alone would always say "shift further" — the binding
 * constraint is occupant comfort, which this data cannot see.
 *
 * Copy contract: observed framing only — no savings/projected/forecast.
 */
import type React from "react"
import { useMemo, useState } from "react"

import { useFormatCurrency } from "@/formatters/use-format-currency"

import { Alert, Box, Stack, Tab, Tabs, Typography } from "@mui/material"

import { Eyebrow } from "../../homepage/atoms"
import { enduseColor } from "../variance-route/enduse-colors"
import type { SetpointScenarios as Scenarios } from "./schedule-misalignment.types"

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"
const VB_W = 720
const VB_H = 260
const PAD_L = 52
const PAD_R = 18
const PAD_T = 22
const PAD_B = 42

export const SetpointScenariosChart: React.FC<{
  data: Scenarios
  periodLabel: string
}> = ({ data, periodLabel }) => {
  const [tab, setTab] = useState(0)
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  const color = enduseColor("cooling")
  const active = data.scenarios[tab]

  const geom = useMemo(() => {
    const pts = data.daily
    const temps = pts.map((p) => p.oat_f)
    const loads = pts.map((p) => p.kw)
    const xMin = Math.floor(Math.min(...temps, data.changepoint_f) - 2)
    const xMax = Math.ceil(Math.max(...temps) + 2)
    const yMax = Math.max(...loads) * 1.1
    const plotW = VB_W - PAD_L - PAD_R
    const plotH = VB_H - PAD_T - PAD_B
    const x = (v: number) => PAD_L + ((v - xMin) / (xMax - xMin)) * plotW
    const y = (v: number) => PAD_T + plotH - (v / yMax) * plotH
    // the fitted line: flat to the changepoint, then rising at `slope`
    const lineFor = (cp: number) => {
      const end = data.fit.base_kw + data.fit.slope_kw_per_f * (xMax - cp)
      return `M${x(xMin)},${y(data.fit.base_kw)} L${x(cp)},${y(
        data.fit.base_kw
      )} L${x(xMax)},${y(end)}`
    }
    return { pts, xMin, xMax, yMax, plotW, plotH, x, y, lineFor }
  }, [data])

  if (data.daily.length === 0 || data.scenarios.length === 0) {
    return null
  }

  const shifted = data.changepoint_f + (active?.delta_f ?? 0)

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 3,
        border: 1,
        borderColor: "grey.200",
        bgcolor: "grey.25",
        p: 2.5,
      }}
    >
      <Tabs
        onChange={(_, v: number) => {
          setTab(v)
        }}
        sx={{ minHeight: 36, mb: 1 }}
        value={tab}
        variant="scrollable"
      >
        {data.scenarios.map((s) => (
          <Tab
            key={s.delta_f}
            label={`+${s.delta_f}°F${s.credible ? "" : " *"}`}
            sx={{ minHeight: 36, py: 0.5, fontFamily: MONO }}
          />
        ))}
      </Tabs>

      <Eyebrow>{`Daily load vs outdoor temperature · ${periodLabel}`}</Eyebrow>

      <Box
        aria-label="Daily load against outdoor temperature, with the observed cooling point and the selected scenario"
        component="svg"
        role="img"
        sx={{ display: "block", width: "100%", height: "auto", mt: 0.5 }}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
      >
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line
              stroke="currentColor"
              strokeOpacity={0.12}
              x1={PAD_L}
              x2={VB_W - PAD_R}
              y1={geom.y(geom.yMax * f)}
              y2={geom.y(geom.yMax * f)}
            />
            <text
              fill="currentColor"
              fillOpacity={0.55}
              fontFamily={MONO}
              fontSize={11}
              textAnchor="end"
              x={PAD_L - 6}
              y={geom.y(geom.yMax * f) + 4}
            >
              {`${Math.round(geom.yMax * f)} kW`}
            </text>
          </g>
        ))}

        {/* the avoided wedge: between the fitted line and the shifted one */}
        <path
          d={`${geom.lineFor(data.changepoint_f)} L${geom.x(geom.xMax)},${geom.y(
            data.fit.base_kw + data.fit.slope_kw_per_f * (geom.xMax - shifted)
          )} L${geom.x(shifted)},${geom.y(data.fit.base_kw)} L${geom.x(
            data.changepoint_f
          )},${geom.y(data.fit.base_kw)} Z`}
          fill={color}
          fillOpacity={0.18}
        />

        {geom.pts.map((p) => (
          <circle
            cx={geom.x(p.oat_f)}
            cy={geom.y(p.kw)}
            fill="currentColor"
            fillOpacity={0.28}
            key={`${p.oat_f}-${p.kw}`}
            r={2.5}
          />
        ))}

        <path
          d={geom.lineFor(data.changepoint_f)}
          fill="none"
          stroke={color}
          strokeWidth={2}
        />
        <path
          d={geom.lineFor(shifted)}
          fill="none"
          stroke={color}
          strokeDasharray="5 3"
          strokeOpacity={0.75}
          strokeWidth={2}
        />

        {/* the observed cooling point */}
        <line
          stroke="currentColor"
          strokeDasharray="2 3"
          strokeOpacity={0.45}
          x1={geom.x(data.changepoint_f)}
          x2={geom.x(data.changepoint_f)}
          y1={PAD_T}
          y2={PAD_T + geom.plotH}
        />
        <text
          fill="currentColor"
          fillOpacity={0.7}
          fontFamily={MONO}
          fontSize={11}
          textAnchor="middle"
          x={geom.x(data.changepoint_f)}
          y={VB_H - PAD_B + 16}
        >
          {`${data.changepoint_f.toFixed(0)}°F`}
        </text>
        <text
          fill="currentColor"
          fillOpacity={0.5}
          fontFamily={MONO}
          fontSize={10}
          textAnchor="middle"
          x={geom.x(data.changepoint_f)}
          y={VB_H - PAD_B + 30}
        >
          observed cooling point
        </text>
        <text
          fill={color}
          fontFamily={MONO}
          fontSize={11}
          textAnchor="middle"
          x={geom.x(shifted)}
          y={PAD_T - 6}
        >
          {`+${active?.delta_f ?? 0}°F → ${shifted.toFixed(0)}°F`}
        </text>
      </Box>

      {active ? (
        <Stack
          direction="row"
          spacing={2.5}
          sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 1 }}
          useFlexGap
        >
          <Typography sx={{ fontWeight: "bold" }} variant="body2">
            {`${formatCurrency(active.dollars_unoccupied)} closed hours`}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {`${formatCurrency(active.dollars_occupied)} open hours`}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {`${Math.round(active.kwh_total).toLocaleString()} kWh`}
          </Typography>
        </Stack>
      ) : null}

      {active && !active.credible ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {`Beyond what this fit can support. The cooling point is only located
          to about ±${data.credible_shift_f}°F, so a ${active.delta_f}°F shift is
          extrapolation — this is about our confidence, not about comfort.`}
        </Alert>
      ) : null}

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        <Typography color="text.secondary" variant="caption">
          <b>Closed hours</b> is part of the after-hours potential savings
          above, not extra — the same energy seen as a setpoint problem rather
          than a schedule one.
          <b> Open hours</b> is separate: this detector only looks at closed
          hours, so that part is additional.
        </Typography>
        {data.rate_fallback ? (
          <Typography color="text.secondary" variant="caption">
            <b>Tariff unavailable</b> — dollar figures are a flat-rate estimate.
            The load and the cooling point are unaffected.
          </Typography>
        ) : null}
        <Typography color="text.secondary" variant="caption">
          Energy only; no demand charge assumed. How far a setpoint can move is
          a comfort question this data cannot answer — the marked limit is where
          our own estimate stops being trustworthy.
        </Typography>
      </Stack>
    </Box>
  )
}
